import React, { useEffect, useState } from 'react';
import { Modal, Box } from '@mui/material';

import { Loading, SimpleButton, Label, ErrorMessage } from '@/src/ui';
import { apiRequest } from '@/src/services';
import QuestionDetailsResponsesTable from './QuestionDetailsResponseTable';
import type {
    ClassMatrixQuestionDetailsDTO,
    ClassMatrixQuestionDetailsPartialDTO,
    GetClassMatrixQuestionDetailsParamsDTO,
} from '../models/classMatrixDTOs';
import { type QtiQuestionDTO, QuestionRenderer } from '@/src/lib';
import type { ClassMatrixReportFilterState } from '../models/filterDTOs';

type Props = {
    open: boolean;
    onClose: () => void;
    filterState: ClassMatrixReportFilterState;
    questionId: number | null;
};

const QuestionDetailsModal: React.FC<Props> = ({ open, onClose, filterState, questionId }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');
    const [data, setData] = useState<ClassMatrixQuestionDetailsDTO | null>(null);
    const [openKey, setOpenKey] = useState<string>('all');

    const handleQuestionClick = async (qid: number) => {
        if (
            !filterState.districtId ||
            !filterState.schoolId ||
            !filterState.classId ||
            !filterState.examId
        ) {
            setError('Please select District, School, Class, and Exam first.');
            setLoading(false);
            setData(null);
            return;
        }

        const q: GetClassMatrixQuestionDetailsParamsDTO = {
            districtId: Number(filterState.districtId),
            schoolId: Number(filterState.schoolId),
            gradeLevelMasterId: Number(filterState.gradeLevelMasterId),
            classId: Number(filterState.classId),
            examId: Number(filterState.examId),
            questionId: Number(qid),
        };

        setLoading(true);
        setError('');
        setData(null);
        try {
            // Get student response buckets
            const studentAnswerRequestUrl =
                `reports/classMatrix/questionDetails?` +
                `districtId=${q.districtId}` +
                `&schoolId=${q.schoolId}` +
                `&gradeLevelMasterId=${q.gradeLevelMasterId}` +
                `&classId=${q.classId}` +
                `&examId=${q.examId}` +
                `&questionId=${q.questionId}`;
            const studentAnswerRes = await apiRequest<ClassMatrixQuestionDetailsPartialDTO>(
                'GET',
                studentAnswerRequestUrl
            );

            // Get question info
            const questionRes = await apiRequest<QtiQuestionDTO>(
                'get',
                `/assessments/questions/${q.questionId}`
            );
            const newData: ClassMatrixQuestionDetailsDTO = {
                ...studentAnswerRes.data,
                questionData: questionRes.data,
            };

            setData(newData);
        } catch (e: any) {
            setError(e?.message ? String(e.message) : 'Failed to load question details.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!open) {
            setLoading(false);
            setError('');
            setData(null);
            return;
        }
        if (questionId) {
            handleQuestionClick(questionId);
        } else {
            setLoading(false);
            setError('Please select District, School, Class, and Exam first.');
            setData(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, questionId]);

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 3,
                    width: 'min(1200px, 96vw)',
                    maxHeight: '90vh',
                    borderRadius: 2,
                    overflow: 'auto',
                }}
            >
                <div
                    className="flex items-start justify-between gap-3 pb-3"
                    style={{ background: 'var(--pm-panel, transparent)' }}
                >
                    <div className="flex items-center gap-2">
                        <h2 className="text-lg font-semibold">
                            {data ? `Question Details: ${data.title}` : 'Question Details'}
                        </h2>
                    </div>
                    <SimpleButton onClick={onClose} variant="text" color="primary">
                        ✕
                    </SimpleButton>
                </div>

                {!loading && data && (
                    <div className="mb-4">
                        <div>Standards:</div>
                        <div className="mt-1 flex flex-wrap gap-2">
                            {data.standards.length === 0 ? (
                                <span>None assigned</span>
                            ) : (
                                data.standards.map((standard) => {
                                    return (
                                        <span
                                            key={standard.id}
                                            className="inline-block rounded"
                                            title={standard.name}
                                        >
                                            {standard.name} - {standard.practiceLessonName}
                                        </span>
                                    );
                                })
                            )}
                        </div>
                    </div>
                )}

                {loading && <Loading message="Loading question details..." size="md" />}

                {!loading && error && (
                    <div className="mt-3">
                        <ErrorMessage error={error} />
                    </div>
                )}

                {!loading && !error && data && (
                    <div className="mt-4 space-y-6">
                        <section
                            className="rounded-2xl border border-gray-200 overflow-auto"
                            aria-label="Correct answer"
                            style={{ background: 'var(--pm-surface, #fff)' }}
                        >
                            <header
                                className="px-4 py-3 border-b border-gray-200 flex items-center justify-between"
                                style={{ background: 'var(--pm-primary)', color: 'white' }}
                            >
                                <div className="flex items-center gap-2">
                                    <h3 className="font-medium">Correct Answer</h3>
                                </div>
                            </header>

                            <div className="p-4">
                                <QuestionRenderer
                                    question={data.questionData}
                                    loadedUserResponse={data.questionData.correctAnswer}
                                    stimMode="sev"
                                    options={{
                                        disabled: true,
                                        language: 'en',
                                    }}
                                />
                            </div>
                        </section>

                        <section
                            className="rounded-2xl border border-gray-200 overflow-auto"
                            aria-label="Student responses"
                            style={{ background: 'var(--pm-surface, #fff)' }}
                        >
                            <header
                                className="px-4 py-3 mb-3 border-b border-gray-200"
                                style={{ background: 'var(--pm-primary)', color: 'white' }}
                            >
                                <h3 className="text-base font-semibold">Student Response Data</h3>
                                <p className="text-sm mt-1">
                                    Summary of how students responded to this question
                                </p>
                            </header>
                            <div className="px-4 pb-4">
                                <div className="mb-2 flex items-center gap-2 text-sm">
                                    <Label color="#D1FADF" sx={{ fontSize: 12 }}>
                                        Correct
                                    </Label>
                                    <Label color="#FEE4E2" sx={{ fontSize: 12 }}>
                                        Incorrect
                                    </Label>
                                </div>
                                <div className="mb-2 text-sm text-gray-700">
                                    <b>{data.totalResponses}</b> responses | {data.correctCount}{' '}
                                    correct | {data.incorrectCount} incorrect
                                </div>

                                <PercentBar
                                    total={data.totalResponses}
                                    correct={data.correctCount}
                                />

                                <QuestionDetailsResponsesTable
                                    data={data}
                                    openKey={openKey}
                                    setOpenKey={setOpenKey}
                                />
                            </div>
                        </section>
                    </div>
                )}
            </Box>
        </Modal>
    );
};

export default QuestionDetailsModal;

const PercentBar: React.FC<{ total: number; correct: number }> = ({ total, correct }) => {
    const base = Math.max(1, total);
    const correctPct = Math.round((correct / base) * 100);
    const incorrectPct = 100 - correctPct;
    return (
        <div className="mb-3 h-3 w-full overflow-hidden rounded bg-gray-100">
            <div className="flex h-full w-full">
                <div style={{ width: `${correctPct}%` }} className="h-full bg-emerald-500" />
                <div style={{ width: `${incorrectPct}%` }} className="h-full bg-rose-400" />
            </div>
        </div>
    );
};
